import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScriptCard } from "@/components/dashboard/ScriptCard";
import { FileText, BookOpen, Users, Upload, Check, X, ArrowLeft, Download, Loader2 } from "lucide-react";
import { v4 as uuid } from "uuid";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { SynopsisCard } from "@/components/dashboard/SynopsisCard";
import { CharacterCardLite } from "@/components/dashboard/CharacterCardLite";
import { fetchUserSynopses } from "@/services/synopsis-service";
import { fetchUserSavedCharacters } from "@/services/character-save-service";
import { cacheUserScriptsOffline } from "@/services/offline-cache-service";
import { ScriptCreationDialog } from "@/components/common/ScriptCreationDialog";
import { ScriptUploadDialog } from "@/components/dashboard/ScriptUploadDialog";
import { UserProfileCard } from "@/components/dashboard/UserProfileCard";
import { SeriesFolderCard } from "@/components/dashboard/SeriesFolderCard";
import { usePendingInvitations } from "@/hooks/usePendingInvitations";
import { useInvitationActions } from "@/hooks/useInvitationActions";
import { useSeriesExport } from "@/hooks/useSeriesExport";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Inline export menu used in the open folder view header
function FolderExportMenu({ seriesName, episodes }: { seriesName: string; episodes: { id: string; title: string }[] }) {
  const { exportAllAsPDF, exportIndividualPDFs } = useSeriesExport();
  const [isExporting, setIsExporting] = useState(false);

  const handleAll = async () => {
    setIsExporting(true);
    await exportAllAsPDF(seriesName, episodes);
    setIsExporting(false);
  };

  const handleIndividual = async () => {
    setIsExporting(true);
    await exportIndividualPDFs(seriesName, episodes);
    setIsExporting(false);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" className="gap-2 bg-naija-green hover:bg-naija-green-dark text-white font-medium shadow-sm" disabled={isExporting}>
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin text-white" />
          ) : (
            <Download className="h-4 w-4 text-white" />
          )}
          Export Episodes
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 bg-card border-border shadow-md">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Export {episodes.length} Episode{episodes.length !== 1 ? "s" : ""}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer gap-2 focus:bg-accent focus:text-accent-foreground" onClick={handleAll}>
          <BookOpen className="h-4 w-4 text-naija-green" />
          <div>
            <p className="font-medium text-sm">All Episodes — One PDF</p>
            <p className="text-xs text-muted-foreground">Single file with all episodes</p>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer gap-2 focus:bg-accent focus:text-accent-foreground" onClick={handleIndividual}>
          <FileText className="h-4 w-4 text-blue-500" />
          <div>
            <p className="font-medium text-sm">Individual Episode PDFs</p>
            <p className="text-xs text-muted-foreground">One PDF per episode</p>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}



// Define simplified Script type for Dashboard use
interface DashboardScript {
  id: string;
  title: string;
  genre?: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [scripts, setScripts] = useState<DashboardScript[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sharedScripts, setSharedScripts] = useState<DashboardScript[]>([]);
  const [isLoadingShared, setIsLoadingShared] = useState(true);
  const [scriptStats, setScriptStats] = useState({
    totalScripts: 0,
    totalCharacters: 0,
    totalScenes: 0,
    totalSynopses: 0,
    lastActive: 'Today'
  });
  const [synopses, setSynopses] = useState([]);
  const [savedCharacters, setSavedCharacters] = useState([]);
  const [showScriptCreationDialog, setShowScriptCreationDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);

  const { pendingInvitations, isLoading: isLoadingInvitations } = usePendingInvitations();
  const { acceptInvitation, rejectInvitation, isLoading: isInvokingAction } = useInvitationActions();

  const handleAcceptInvite = async (inviteId: string) => {
    const scriptId = await acceptInvitation(inviteId);
    if (scriptId) {
      setRefreshKey(prev => prev + 1);
    }
  };

  const handleRejectInvite = async (inviteId: string) => {
    const success = await rejectInvitation(inviteId);
    if (success) {
      setRefreshKey(prev => prev + 1);
    }
  };

  useEffect(() => {
    const fetchScripts = async () => {
      try {
        setIsLoading(true);

        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          toast({
            title: "Authentication required",
            description: "Please login to view your scripts",
            variant: "destructive"
          });
          navigate("/auth");
          return;
        }

        // Fetch user's scripts and sort by updated_at then created_at
        const { data: scriptsData, error: scriptsError } = await supabase
          .from('scripts')
          .select('*')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false })
          .order('created_at', { ascending: false });

        if (scriptsError) {
          console.error('Error fetching scripts:', scriptsError);
          toast({
            title: "Error",
            description: "Failed to load scripts",
            variant: "destructive"
          });
        } else if (scriptsData) {
          setScripts(scriptsData as DashboardScript[]);
          
          // Update stats
          setScriptStats(prev => ({
            ...prev,
            totalScripts: scriptsData?.length || 0,
          }));

          // Trigger background offline caching of scripts
          setTimeout(() => {
            cacheUserScriptsOffline(user.id);
          }, 2000); // Wait 2 seconds to not block initial render

        }

        // Fetch character count - fix: use .in() with array of script IDs
        if (scriptsData && scriptsData.length > 0) {
          const scriptIds = scriptsData.map(s => s.id);
          const { count: characterCount, error: characterError } = await supabase
            .from('characters')
            .select('*', { count: 'exact', head: true })
            .in('script_id', scriptIds);

          if (!characterError && characterCount !== null) {
            setScriptStats(prev => ({
              ...prev,
              totalCharacters: characterCount
            }));
          }

          // Fetch scenes count - fix: use .in() with array of script IDs
          const { count: scenesCount, error: scenesError } = await supabase
            .from('scenes')
            .select('*', { count: 'exact', head: true })
            .in('script_id', scriptIds);

          if (!scenesError && scenesCount !== null) {
            setScriptStats(prev => ({
              ...prev,
              totalScenes: scenesCount
            }));
          }
        }

        // Fetch synopses count
        const { count: synopsesCount, error: synopsesError } = await supabase
          .from('synopses')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);
        if (!synopsesError && synopsesCount !== null) {
          setScriptStats(prev => ({
            ...prev,
            totalSynopses: synopsesCount
          }));
        }

        // Check last activity
        if (scriptsData && scriptsData.length > 0) {
          const lastUpdated = new Date(scriptsData[0].updated_at);
          const today = new Date();
          const diffDays = Math.floor((today.getTime() - lastUpdated.getTime()) / (1000 * 60 * 60 * 24));
          if (diffDays === 0) {
            setScriptStats(prev => ({ ...prev, lastActive: 'Today' }));
          } else if (diffDays === 1) {
            setScriptStats(prev => ({ ...prev, lastActive: 'Yesterday' }));
          } else {
            setScriptStats(prev => ({ ...prev, lastActive: `${diffDays} days ago` }));
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast({
          title: "Error",
          description: "Failed to load dashboard data",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    const fetchSharedScripts = async () => {
      try {
        setIsLoadingShared(true);

        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setSharedScripts([]);
          return;
        }

        // Get scripts where user is a collaborator (not the owner)
        const { data, error } = await supabase
          .from('script_collaborators')
          .select(`
            script_id,
            created_at,
            scripts (
              id,
              title,
              genre,
              description,
              created_at,
              updated_at,
              user_id
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching shared scripts:', error);
          toast({
            title: "Error",
            description: "Failed to load shared scripts",
            variant: "destructive"
          });
          setSharedScripts([]);
          return;
        }

        // Transform data and filter out scripts owned by user
        const sharedScriptsData = (data || [])
          .filter(item => item.scripts && item.scripts.user_id !== user.id) // Exclude owned scripts
          .map(item => item.scripts as DashboardScript)
          .filter(Boolean);

        console.log('Shared scripts loaded:', sharedScriptsData.length);
        setSharedScripts(sharedScriptsData);
      } catch (error) {
        console.error("Error fetching shared scripts:", error);
        setSharedScripts([]);
      } finally {
        setIsLoadingShared(false);
      }
    };

    fetchScripts();
    fetchSharedScripts();
  }, [toast, navigate, refreshKey]);

  useEffect(() => {
    const fetchExtraContent = async () => {
      try {
        // Get user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Fetch synopses directly with proper error handling
        const { data: synopsesData, error: synopsesError } = await supabase
          .from('synopses')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (synopsesError) {
          console.error('Error fetching synopses:', synopsesError);
        } else {
          setSynopses(synopsesData || []);
        }

        // Fetch saved characters directly with proper error handling
        const { data: charactersData, error: charactersError } = await supabase
          .from('saved_characters')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (charactersError) {
          console.error('Error fetching saved characters:', charactersError);
        } else {
          setSavedCharacters(charactersData || []);
        }
      } catch (err) {
        console.error('Error in fetchExtraContent:', err);
      }
    };

    fetchExtraContent();
  }, []);

  const handleCreateNewScript = () => {
    setShowScriptCreationDialog(true);
  };

  const handleUploadScript = () => {
    setShowUploadDialog(true);
  };

  const handleGenerateAIContent = (contentType: string) => {
    toast({
      title: "AI Assistant",
      description: `Generating ${contentType}...`
    });

    // In a real implementation, this would call an AI service
    setTimeout(() => {
      toast({
        title: "AI Suggestions Ready",
        description: `Check your ${contentType} suggestions in the editor`
      });
    }, 1500);
  };

  const handleRefreshSynopses = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: synopsesData, error } = await supabase
          .from('synopses')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error refreshing synopses:', error);
        } else {
          setSynopses(synopsesData || []);
        }
      }
    } catch (error) {
      console.error('Error refreshing synopses:', error);
    }
  };

  const renderScriptCards = (scripts: DashboardScript[]) => {
    return scripts.map(script => (
      <ScriptCard
        key={script.id}
        script={script}
        onUpdate={() => {
          setRefreshKey(prev => prev + 1);
        }}
      />
    ));
  };

  const extractSeriesInfo = (title: string) => {
    const match = title.match(/^(.*) - S(\d+)E(\d+)(?:: (.*))?$/);
    if (match) {
      return {
        isSeries: true,
        seriesName: match[1].trim(),
        season: parseInt(match[2]),
        episode: parseInt(match[3]),
        episodeName: match[4]?.trim() || ''
      };
    }
    return { isSeries: false };
  };

  const getGroupedScripts = (scriptsToGroup: DashboardScript[]) => {
    const groups = new Map<string, DashboardScript[]>();
    const standalone: DashboardScript[] = [];

    scriptsToGroup.forEach(script => {
      const info = extractSeriesInfo(script.title);
      if (info.isSeries && info.seriesName) {
        if (!groups.has(info.seriesName)) {
          groups.set(info.seriesName, []);
        }
        groups.get(info.seriesName)!.push(script);
      } else {
        standalone.push(script);
      }
    });

    // Sort episodes inside groups
    groups.forEach((episodes) => {
      episodes.sort((a, b) => {
        const infoA = extractSeriesInfo(a.title);
        const infoB = extractSeriesInfo(b.title);
        return (infoA.episode || 0) - (infoB.episode || 0);
      });
    });

    return { groups, standalone };
  };

  const renderGroupedCards = (scriptsToRender: DashboardScript[]) => {
    if (selectedFolder) {
      const { groups } = getGroupedScripts(scriptsToRender);
      const folderEpisodes = groups.get(selectedFolder) || [];
      return (
        <div className="space-y-6 col-span-full">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-4">
            <div className="flex flex-wrap items-center gap-4">
              <Button variant="ghost" onClick={() => setSelectedFolder(null)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
              <div className="h-6 w-px bg-border hidden sm:block" />
              <div>
                <h2 className="text-xl font-bold text-foreground">{selectedFolder}</h2>
                <p className="text-sm text-muted-foreground">{folderEpisodes.length} Episodes</p>
              </div>
              <div className="ml-2">
                <FolderExportMenu seriesName={selectedFolder} episodes={folderEpisodes} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {renderScriptCards(folderEpisodes)}
          </div>
        </div>
      );
    }


    const { groups, standalone } = getGroupedScripts(scriptsToRender);
    const renderItems: React.ReactNode[] = [];

    // Add folders
    Array.from(groups.entries()).forEach(([seriesName, episodes]) => {
      renderItems.push(
        <SeriesFolderCard
          key={`folder-${seriesName}`}
          seriesName={seriesName}
          episodes={episodes}
          onClick={() => setSelectedFolder(seriesName)}
        />
      );
    });

    // Add standalone scripts
    renderItems.push(...standalone.map(script => (
      <ScriptCard
        key={script.id}
        script={script}
        onUpdate={() => setRefreshKey(prev => prev + 1)}
      />
    )));

    // Sort render items by the most recently updated item inside them
    renderItems.sort((a: any, b: any) => {
      const dateA = new Date(a.props.script ? a.props.script.updated_at : a.props.episodes[0].updated_at).getTime();
      const dateB = new Date(b.props.script ? b.props.script.updated_at : b.props.episodes[0].updated_at).getTime();
      return dateB - dateA;
    });

    return <>{renderItems}</>;
  };

  const renderLoadingSkeletons = () => {
    return [1, 2, 3].map(i => (
      <Card key={i} className="overflow-hidden transition-all duration-300">
        <div className="h-1 bg-gray-200 w-full"></div>
        <CardHeader className="pb-2">
          <Skeleton className="h-6 w-3/4" />
        </CardHeader>
        <CardContent className="text-sm">
          <Skeleton className="h-10 w-full mb-4" />
          <Skeleton className="h-4 w-2/3" />
        </CardContent>
        <CardFooter className="pt-2 border-t">
          <Skeleton className="h-8 w-16" />
        </CardFooter>
      </Card>
    ));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-400">Your Dashboard</h1>
        <div className="flex gap-2">
          <Button onClick={handleUploadScript} variant="outline" className="border-naija-green text-naija-green hover:bg-naija-green/10">
            <Upload className="mr-2 h-4 w-4" />
            Upload Script
          </Button>
          <Button onClick={handleCreateNewScript} className="bg-naija-green hover:bg-naija-green-dark text-white">
            New Script
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Profile Card */}
        <div className="lg:col-span-1">
          <UserProfileCard />
        </div>

        {/* Stats Card */}
        <Card className="lg:col-span-2">
          <CardHeader className="">
            <CardTitle>Creative Stats</CardTitle>
            <CardDescription>Your professional writing activity summary</CardDescription>
          </CardHeader>
          <CardContent className="">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-naija-green/10 rounded-lg p-4">
                <p className="text-sm font-medium text-naija-green-dark">Total Scripts</p>
                <p className="text-3xl font-bold text-naija-green">{scriptStats.totalScripts}</p>
              </div>
              <div className="bg-naija-gold/10 rounded-lg p-4">
                <p className="text-sm font-medium text-naija-earth">Characters</p>
                <p className="text-3xl font-bold text-naija-gold-dark">{scriptStats.totalCharacters}</p>
              </div>
              <div className="bg-naija-accent/10 rounded-lg p-4">
                <p className="text-sm font-medium text-naija-accent">Scenes Written</p>
                <p className="text-3xl font-bold text-naija-accent">{scriptStats.totalScenes}</p>
              </div>
              <div className="bg-blue-100 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-600">Synopses</p>
                <p className="text-3xl font-bold text-blue-800">{scriptStats.totalSynopses}</p>
              </div>
            </div>
            <div className="mt-4 bg-gray-100 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-600">Last Active</p>
              <p className="text-lg font-bold text-gray-800">{scriptStats.lastActive}</p>
            </div>
          </CardContent>
        </Card>


      </div>

      <Tabs defaultValue="recent" className="w-full">
        <TabsList className="mb-6 w-full sm:w-auto flex overflow-x-auto sm:overflow-visible">
          <TabsTrigger value="recent" className="flex-1 sm:flex-auto">Recent Scripts</TabsTrigger>
          <TabsTrigger value="all" className="flex-1 sm:flex-auto">All Scripts</TabsTrigger>
          <TabsTrigger value="shared" className="flex-1 sm:flex-auto relative">
            Shared With Me
            {pendingInvitations.length > 0 && (
              <Badge variant="destructive" className="absolute -top-2 -right-2 px-1.5 min-w-[1.25rem] h-5 flex items-center justify-center rounded-full text-[10px]">
                {pendingInvitations.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="generated" className="flex-1 sm:flex-auto">AI Generated</TabsTrigger>
        </TabsList>

        <TabsContent value="recent" className="mt-6">
          {selectedFolder ? (
            renderGroupedCards(scripts)
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {isLoading ? renderLoadingSkeletons() : scripts.length === 0 ? (
                <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <FileText className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-300 mb-2">No scripts yet</h3>
                  <p className="text-sm text-gray-500 max-w-sm">
                    Create your first professional script to start writing your screenplay
                  </p>
                  <Button onClick={handleCreateNewScript} className="mt-4 bg-naija-green hover:bg-naija-green-dark text-white">
                    Create Professional Script
                  </Button>
                </div>
              ) : renderGroupedCards(scripts)}
            </div>
          )}
        </TabsContent>


        <TabsContent value="all" className="mt-6">
          {selectedFolder ? (
            renderGroupedCards(scripts)
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {isLoading ? renderLoadingSkeletons() : scripts.length === 0 ? (
                <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <FileText className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-300 mb-2">No scripts yet</h3>
                  <p className="text-sm text-gray-500 max-w-sm">
                    Create your first professional script to start writing your screenplay
                  </p>
                  <Button onClick={handleCreateNewScript} className="mt-4 bg-naija-green hover:bg-naija-green-dark text-white">
                    Create Professional Script
                  </Button>
                </div>
              ) : renderGroupedCards(scripts)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="shared" className="mt-6 space-y-8">
          {/* Pending Invitations Section */}
          {pendingInvitations.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Badge variant="destructive" className="bg-red-500">{pendingInvitations.length}</Badge>
                Pending Invitations
              </h3>
              <div className="flex flex-col gap-2">
                {pendingInvitations.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between gap-4 px-4 py-3 rounded-lg border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/15 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-amber-400 uppercase tracking-wide mb-0.5">
                        Collaboration Invite
                      </p>
                      <p className="text-sm text-white truncate">
                        <span className="font-semibold">{invite.inviterEmail}</span>
                        <span className="text-gray-300"> invited you to collaborate</span>
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        className="h-7 px-3 text-xs bg-naija-green hover:bg-naija-green-dark text-white"
                        onClick={() => handleAcceptInvite(invite.id)}
                        disabled={isInvokingAction}
                      >
                        <Check className="w-3 h-3 mr-1" />
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-3 text-xs border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                        onClick={() => handleRejectInvite(invite.id)}
                        disabled={isInvokingAction}
                      >
                        <X className="w-3 h-3 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            {pendingInvitations.length > 0 && (
              <h3 className="text-lg font-semibold mb-4 text-gray-700">Active Collaborations</h3>
            )}
            {isLoadingShared ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {renderLoadingSkeletons()}
              </div>
            ) : sharedScripts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">No shared scripts yet</h3>
                <p className="text-sm text-gray-500 max-w-md mt-1">
                  When someone shares a script with you, it will appear here for collaborative editing.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {renderScriptCards(sharedScripts)}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="generated" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Professional Treatments Panel */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="h-5 w-5 text-naija-green-dark" />
                <div className="text-lg font-semibold text-naija-green-dark">Professional Treatments & Synopses</div>
              </div>
              {synopses.length === 0 ? (
                <div className="text-gray-400 px-2 py-8 text-center">
                  <BookOpen className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No generated treatments yet.</p>
                  <p className="text-sm mt-1">Use AI Script Generator to create professional treatments.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {synopses.map((syn: any) => (
                    <SynopsisCard
                      key={syn.id}
                      id={syn.id}
                      title={syn.title}
                      content={syn.content}
                      createdAt={syn.created_at}
                      onUpdate={handleRefreshSynopses}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Characters Panel */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Users className="h-5 w-5 text-naija-gold-dark" />
                <div className="text-lg font-semibold text-naija-gold-dark">Generated Characters</div>
              </div>
              {savedCharacters.length === 0 ? (
                <div className="text-gray-400 px-2 py-8 text-center">
                  <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No generated characters yet.</p>
                  <p className="text-sm mt-1">Save characters from your AI-generated treatments.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {savedCharacters.map((char: any) => (
                    <CharacterCardLite
                      key={char.id}
                      name={char.name}
                      description={char.description}
                      background={char.background}
                      traits={char.traits}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <ScriptCreationDialog
        open={showScriptCreationDialog}
        onOpenChange={setShowScriptCreationDialog}
      />

      <ScriptUploadDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
      />
    </div>
  );
}
